import React from "react";
import styled from "styled-components";

import { ExpressionConfiguration } from "../../../rule-engine-core/Expressions/ExpressionConfiguration";
import { IntersectionRuleConfiguration } from "../../../rule-engine-core/Rule/Intersection/IntersectionRuleConfiguration";
import { IntersectionRuleColumn } from "./IntersectionRuleColumn";

import { RuleTableHeaderActionColumn, RuleTableHeaderSplit, RuleTableIndexColumn } from "../../DesignUI/CommonStyles";
import { PlusGrayIcon } from "../../../CommonUI/Icon/PlusGrayIcon";
import { StateManager } from "../../../rule-engine-core/Common/StateManager";
import { ObserveState } from "../../../CommonUI/StateManagement/ObserveState";
import { TestContext } from "../../../CommonUI/Testing/TestContext";
import { UIStyleConstants } from "../../../rule-engine-core/Common/UIStyleConstants";

interface IntersectionRuleHeaderProps {
    expressions: ExpressionConfiguration[];
    ruleDefinition: IntersectionRuleConfiguration;
}

export const IntersectionRuleHeader = (props: IntersectionRuleHeaderProps) => {
    const { expressions, ruleDefinition } = props;

    const testContext = React.useContext(TestContext);

    const onAddNewXAxisExpression = () => {
        ruleDefinition.addXAxisExpression();
        StateManager.propertyChanged(ruleDefinition, "xaxisExpressions");
    };

    if (testContext) {
        return (
            <>
                <RuleTableIndexColumn
                    x={0}
                    y={0}
                />

                <IntersectionRuleTitle isTest={true}>Intersection Table</IntersectionRuleTitle>

                <RuleTableHeaderSplit
                    height={44}
                    width={1}
                    x={2}
                    y={0}
                />

                {expressions.map((exp, index) => (
                    <IntersectionRuleColumn
                        expression={exp}
                        key={exp._id}
                        ruleDefinition={ruleDefinition}
                        direction={"x"}
                        index={index}
                    />
                ))}
                <RuleTableHeaderActionColumn
                    data-testid="btn-intersectionrule-column-add"
                    disabled={!ruleDefinition.canChangeOutputs}
                ></RuleTableHeaderActionColumn>
                <RuleTableIndexColumn
                    height={1}
                    x={0}
                    y={1}
                />

                <RuleTableHeaderSplit
                    width={24}
                    x={1}
                    y={1}
                />

                <div />

                {expressions.map((_, idx) => (
                    <RuleTableHeaderSplit
                        key={idx}
                        y={1}
                        x={idx + 3}
                    />
                ))}

                <RuleTableHeaderSplit width={24} />
            </>
        );
    }

    return (
        <>
            <RuleTableIndexColumn />

            <IntersectionRuleTitle>Intersection Table</IntersectionRuleTitle>

            <RuleTableHeaderSplit
                height={44}
                width={1}
            />

            {expressions.map((exp, index) => (
                <IntersectionRuleColumn
                    expression={exp}
                    key={exp._id}
                    ruleDefinition={ruleDefinition}
                    direction={"x"}
                    index={index}
                />
            ))}

            <ObserveState
                listenTo={ruleDefinition}
                properties={["canChangeOutputs"]}
                control={() => (
                    <RuleTableHeaderActionColumn
                        onClick={onAddNewXAxisExpression}
                        data-testid="btn-intersectionrule-column-add"
                        disabled={!ruleDefinition.canChangeOutputs}
                    >
                        <PlusGrayIcon />
                    </RuleTableHeaderActionColumn>
                )}
            />

            <RuleTableIndexColumn height={1} />

            <RuleTableHeaderSplit width={24} />

            <div />

            {expressions.map((_, idx) => (
                <RuleTableHeaderSplit key={idx} />
            ))}

            <RuleTableHeaderSplit width={24} />
        </>
    );
};

export const IntersectionRuleTitle = styled.div<{ isTest?: boolean }>`
    display: flex;
    align-items: center;
    justify-content: center;

    padding-left: 15px;
    padding-right: 15px;

    font-size: 16px;
    font-style: normal;
    font-weight: 600;
    line-height: 22px;
    letter-spacing: 0em;
    text-align: left;

    color: ${UIStyleConstants.TEXT_COLOR_DEFAULT};

    ${(props) =>
        props.isTest &&
        `
        grid-column-start: 2;
        grid-column-end: 3;

        grid-row-start: 1;
        grid-row-end: 2;
    `}
`;
